import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
} from "react";

import { createPortal } from "react-dom";

import { useAccountContext } from "~/v2/providers/AccountProvider";
import { WidgetTitleEndSlotContext } from "~/v3-poc/components/Widget";
import { useAtdThemeContext } from "~/v3-poc/features/user-preferences";
import { ClosePositionDialog } from "./ClosePositionDialog.connector";
import { LiquidateAllDialog } from "./LiquidateAllDialog.connector";
import { PositionDetailPanelPresentation } from "./PositionDetailPanel.pres";
import { PositionsEmbeddedTicket } from "./PositionsEmbeddedTicket";
import { PositionsToolbarPresentation } from "./PositionsToolbar.pres";
import {
  type PositionsWidgetControllerResult,
  usePositionsWidgetController,
} from "./PositionsWidget.controller";
import {
  PositionsOpenCountLabel,
  PositionsWidgetPresentation,
} from "./PositionsWidget.pres";
import type { PositionsWidgetRow } from "./PositionsWidget.types";
import { RollPositionDialog } from "./RollPositionDialog.connector";

export interface PositionsWidgetProps {
  id: string;
}

const renderOpenCountSlot = ({
  openCount,
  rowsLength,
  titleEndSlot,
}: {
  openCount: number | undefined;
  rowsLength: number;
  titleEndSlot: HTMLElement | null;
}): ReactNode => {
  if (openCount !== undefined && titleEndSlot) {
    return createPortal(
      <PositionsOpenCountLabel shown={rowsLength} total={openCount} />,
      titleEndSlot,
    );
  }
  return null;
};

/**
 * Positions widget: real account data, in-widget split-screen trade ticket,
 * DELETE close for selected rows, and DELETE liquidate-all.
 */
export const PositionsWidget = ({ id }: PositionsWidgetProps): ReactElement => {
  const { currentAccount, product } = useAccountContext();
  const {
    widget,
    toolbar,
    closeDialog,
    rollDialog,
    liquidateDialog,
    embeddedTicket,
    detail,
  }: PositionsWidgetControllerResult = usePositionsWidgetController({
    id,
    accountId: currentAccount?.id,
    product,
  });
  const { theme } = useAtdThemeContext();
  const titleEndSlot = useContext(WidgetTitleEndSlotContext);

  const renderDetailPanel = useCallback(
    (row: PositionsWidgetRow) => {
      if (row.id !== detail.rowId) {
        return null;
      }
      return (
        <PositionDetailPanelPresentation
          row={row}
          dayHigh={detail.dayHigh}
          dayLow={detail.dayLow}
          isWatched={detail.isWatched}
        />
      );
    },
    [detail.dayHigh, detail.dayLow, detail.isWatched, detail.rowId],
  );

  return (
    <PositionsWidgetPresentation
      {...widget}
      renderDetailPanel={renderDetailPanel}
      openCountSlot={renderOpenCountSlot({
        openCount: widget.openCount,
        rowsLength: widget.rows.length,
        titleEndSlot,
      })}
      toolbarSlot={<PositionsToolbarPresentation {...toolbar} />}
      embeddedTicketSlot={
        embeddedTicket ? (
          <PositionsEmbeddedTicket
            theme={theme}
            symbol={embeddedTicket.symbol}
            quantity={embeddedTicket.quantity}
            onClose={embeddedTicket.onClose}
          />
        ) : null
      }
      closeDialogSlot={<ClosePositionDialog {...closeDialog} />}
      rollDialogSlot={<RollPositionDialog {...rollDialog} />}
      liquidateDialogSlot={<LiquidateAllDialog {...liquidateDialog} />}
    />
  );
};
